import React, { FC } from 'react';

import { IntlMessageFormat } from 'intl-messageformat';

import { useIdCounter, useMediaQuery } from '../../hooks';
import { NextButton, PreviousButton } from '../button2';
import { SectionHeader } from '../section-header';
import { VisuallyHidden } from '../visually-hidden';

import {
  ListContainer,
  ListWrapper,
  SideButtonWrapper,
  StyledGrid,
  Wrapper,
} from './menu-carousel.styled';
import { MenuCarouselProps } from './types';

/**
 * A carousel designed as a quickview at a limited number of menu items to cover various scenarios.
 */
const MenuCarousel: FC<MenuCarouselProps> = ({
  id: carouselId,
  header,
  children,
  ctaText,
  ctaHref,
  ctaAs,
  previousButtonTitle,
  nextButtonTitle,
  pageChangeReader,
  handleNextClick,
  handlePreviousClick,
  itemOffset,
  lastItemSlide,
  cardsPerPage,
}) => {
  const isTablet = useMediaQuery('tablet');
  const totalCards = children.length;

  const defaultCarousel = useIdCounter('carousel');
  const uniqueId = carouselId ?? defaultCarousel;

  const hideRightButton = itemOffset + cardsPerPage >= totalCards;
  const handlePageReader = (message = '') => {
    return new IntlMessageFormat(message).format({
      firstItemSlide: itemOffset + 1,
      lastItemSlide,
      totalCards,
    });
  };

  if (!Array.isArray(children) || children.length === 0) {
    return null;
  }

  return (
    <Wrapper aria-labelledby={uniqueId}>
      {header && ctaText && ctaAs && ctaHref && (
        <SectionHeader
          id={'rewards-section-header'}
          variant="call-to-action"
          headerText={header}
          callToActionAs={ctaAs}
          callToActionText={ctaText}
          callToActionLink={ctaHref}
        />
      )}
      {isTablet && (
        <VisuallyHidden aria-live="polite">{handlePageReader(pageChangeReader)}</VisuallyHidden>
      )}
      <StyledGrid>
        <SideButtonWrapper spanTablet={1} spanDesktopLarge={1}>
          <PreviousButton
            title={previousButtonTitle}
            onClick={handlePreviousClick}
            disabled={itemOffset === 0}
          />
        </SideButtonWrapper>
        <ListWrapper spanTablet={6} spanDesktopLarge={10}>
          <ListContainer $offset={itemOffset} $totalCards={totalCards} $cardsPerPage={cardsPerPage}>
            {children}
          </ListContainer>
        </ListWrapper>
        <SideButtonWrapper
          spanTablet={1}
          spanDesktopLarge={1}
          style={{ justifyContent: 'flex-end' }}
        >
          <NextButton
            title={nextButtonTitle}
            onClick={handleNextClick}
            disabled={hideRightButton}
          />
        </SideButtonWrapper>
      </StyledGrid>
    </Wrapper>
  );
};

export default MenuCarousel;
